import getWebsiteUrl from '@cc/shared/utils/getWebsiteUrl';

const extractHTMLForPDF = async (element: HTMLElement) => {
    const clone = element.cloneNode(true) as HTMLElement;

    applyPDFOptimizations(clone);

    const cssRules = extractRelevantCSS(element);

    return createCompleteHTML(clone.outerHTML, cssRules);
}

const applyPDFOptimizations = (element: HTMLElement) => {
    const allElements = [element, ...element.querySelectorAll('*')] as HTMLElement[];

    allElements.forEach(el => {
        const textContent = el.textContent || '';
        if (/\d/.test(textContent)) {
            if (!el.style.fontFamily || el.style.fontFamily.includes('system-ui') || el.style.fontFamily.includes('-apple-system')) {
                el.style.fontFamily = "Arial, sans-serif";
            }
        }

        if (el.style.position === 'fixed') {
            el.style.position = 'absolute';
        }

        ['onclick', 'onmouseover', 'onmouseout'].forEach(attr => el.removeAttribute(attr));
    });
}

const extractRelevantCSS = (element: HTMLElement) => {
    const relevantRules = new Set();
    const allElements = [element, ...element.querySelectorAll('*')];

    try {
        for (const styleSheet of document.styleSheets) {
            try {
                const rules = styleSheet.cssRules || styleSheet.rules;

                for (const rule of rules) {
                    if (rule.type === CSSRule.STYLE_RULE) {
                        if (allElements.some(el => {
                            try {
                                return el.matches(rule.selectorText);
                            } catch (e) {
                                return false;
                            }
                        })) {
                            relevantRules.add(rule.cssText);
                        }
                    }
                }
            } catch (e) {
                console.warn('Cannot access stylesheet:', e);
            }
        }
    } catch (e) {
        console.warn('Error extracting CSS:', e);
    }

    return Array.from(relevantRules).join('\n');
}

const createCompleteHTML = (bodyHTML: string, cssRules = '') => {
    const printStyles = `
        @page {
            size: A4;
            margin: 1.5cm;
        }

        /* Only fix font rendering issues */
        * {
            -webkit-print-color-adjust: exact !important;
            color-adjust: exact !important;
        }

        /* Fix invisible numbers issue */
        span, div, td, th {
            font-variant-numeric: lining-nums;
        }

        #print-button {
            display: none;
        }
    `;

    const sanitizedHTML = bodyHTML
        .replace(/(\d{1,2}[\/\-\.]\d{1,2}[\/\-\.]\d{2,4})/g,
            (match) => `<span style="font-family: Arial, sans-serif;">${match}</span>`);

    return `<!DOCTYPE html>
<html lang="en">
<head>
    <meta charset="UTF-8">
    <title>PDF Content</title>
    <style>${cssRules}</style>
    <style>${printStyles}</style>
</head>
<body>
    ${sanitizedHTML}
</body>
</html>`;
}

const generatePDF = async (element: HTMLElement) => {
    try {
        const content = await extractHTMLForPDF(element);

        const response = await fetch(getWebsiteUrl({ slug: '/api/embed/generate-pdf/' }), {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ content }),
        });

        if (!response.ok) {
            throw new Error(`PDF generation failed: ${response.statusText}`);
        }

        const pdfBlob = await response.blob();
        const url = URL.createObjectURL(pdfBlob);
        window.open(url);
    } catch (error) {
        console.error('PDF generation failed:', error);
    }
}

export default generatePDF;
